/**
 * Brands: who a campaign's emails are from.
 *
 * Swicc, Utility Switch and Savvy Switch each send as themselves, so a lead
 * who signed up on Utility Switch gets email that looks like it came from
 * there. A brand's From address must be on a domain verified in Amazon SES.
 */
import { useState } from 'react';
import { ActionIcon, Alert, Button, Group, Menu, Modal, NumberInput, Stack, Table, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDots, IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { useDeleteEmailBrand, useEmailBrands, useSaveEmailBrand, type EmailBrand } from '../../api/queries';
import { GENTLE_RAMP, describeRamp, parseRamp, showRamp } from './warmup';

export function BrandsTab() {
  const brands = useEmailBrands();
  const remove = useDeleteEmailBrand();
  const [editing, setEditing] = useState<EmailBrand | 'new' | null>(null);
  const list = brands.data?.brands ?? [];

  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Text size="sm" c="dimmed" maw={620}>
          Each brand sends as itself. The From address has to be on a domain verified in Amazon SES,
          such as news.swicc.co.uk.
        </Text>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setEditing('new')}>New brand</Button>
      </Group>
      {brands.error && <Alert color="red" variant="light">{brands.error.message}</Alert>}
      <Table.ScrollContainer minWidth={640}>
        <Table highlightOnHover verticalSpacing={8}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Brand</Table.Th>
              <Table.Th>From</Table.Th>
              <Table.Th>Reply-To</Table.Th>
              <Table.Th>Website</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {!brands.isPending && list.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={5}>
                  <Stack align="center" gap={6} py="xl">
                    <Text size="sm" c="dimmed">No brands yet.</Text>
                    <Button size="xs" variant="light" onClick={() => setEditing('new')}>Add Swicc</Button>
                  </Stack>
                </Table.Td>
              </Table.Tr>
            )}
            {list.map((b) => (
              <Table.Tr key={b.id}>
                <Table.Td><Text size="sm" fw={600}>{b.name}</Text></Table.Td>
                <Table.Td>
                  <Text size="sm">{b.from_name || b.name}</Text>
                  <Text size="xs" c={b.from_email ? 'dimmed' : 'red'}>{b.from_email || 'No From address yet'}</Text>
                </Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{b.reply_to || '-'}</Text></Table.Td>
                <Table.Td><Text size="sm" c="dimmed">{b.website || '-'}</Text></Table.Td>
                <Table.Td>
                  <Menu position="bottom-end" withinPortal>
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray" aria-label={`More for ${b.name}`}><IconDots size={16} /></ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconPencil size={14} />} onClick={() => setEditing(b)}>Edit</Menu.Item>
                      <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={() => remove.mutate(b.id, {
                        onError: (e) => notifications.show({ color: 'red', title: 'Not deleted', message: (e as Error).message }),
                      })}>Delete</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      {editing && <BrandModal brand={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </Stack>
  );
}

function BrandModal({ brand, onClose }: { brand: EmailBrand | null; onClose: () => void }) {
  const save = useSaveEmailBrand();
  const [form, setForm] = useState({
    name: brand?.name ?? '',
    from_name: brand?.from_name ?? '',
    from_email: brand?.from_email ?? '',
    reply_to: brand?.reply_to ?? '',
    website: brand?.website ?? '',
    unsubscribe_domain: brand?.unsubscribe_domain ?? '',
  });
  const [dailyCap, setDailyCap] = useState<number>(brand?.daily_cap ?? 0);
  const [ramp, setRamp] = useState(showRamp(brand?.warmup));
  const field = (k: keyof typeof form) => ({
    value: form[k],
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.currentTarget.value;
      setForm((f) => ({ ...f, [k]: v }));
    },
  });

  return (
    <Modal opened onClose={onClose} title={brand ? 'Edit brand' : 'New brand'} size="md">
      <Stack>
        <TextInput label="Brand name" placeholder="Swicc" data-autofocus {...field('name')} />
        <Group grow>
          <TextInput label="From name" description="What inboxes show" placeholder="Swicc" {...field('from_name')} />
          <TextInput label="From address" description="On a verified domain" placeholder="hello@news.swicc.co.uk" {...field('from_email')} />
        </Group>
        <TextInput label="Reply-To" description="Optional. Where replies go." placeholder="support@swicc.co.uk" {...field('reply_to')} />
        <TextInput label="Website" description="The unsubscribe page links back here" placeholder="https://swicc.co.uk" {...field('website')} />
        <TextInput
          label="Unsubscribe link domain"
          description="Optional. Unsubscribe links use this domain, so they match your brand. Point it at this server first; it is checked when you save."
          placeholder="u.swicc.co.uk"
          data-testid="unsubscribe-domain"
          {...field('unsubscribe_domain')}
        />
        <Group grow align="flex-start">
          <NumberInput label="Most per day, all campaigns" min={0} thousandSeparator="," value={dailyCap}
            description="0 for no limit" onChange={(v) => setDailyCap(Number(v) || 0)} />
          <TextInput label="Warm-up ramp" placeholder={showRamp(GENTLE_RAMP)} value={ramp}
            description={describeRamp(parseRamp(ramp))}
            onChange={(e) => { const v = e.currentTarget.value; setRamp(v); }} />
        </Group>
        <Text size="xs" c="dimmed">
          For a new sending domain: the most emails a day on day 1, 2, 3 and so on, from when the ramp is
          saved. A changed ramp starts again from day 1.
        </Text>
        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>Cancel</Button>
          <Button loading={save.isPending} disabled={!form.name.trim()} onClick={() => save.mutate({ id: brand?.id, body: { ...form, daily_cap: dailyCap, warmup: parseRamp(ramp) } }, {
            onSuccess: onClose,
            onError: (e) => notifications.show({ color: 'red', title: 'Not saved', message: (e as Error).message }),
          })}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
}
